import { SchemaType } from '../constants/schema.js'

const generateId = () => globalThis.crypto.randomUUID()

const buildBoundedContextsMutationInput = (workflowId, expectedVersion, boundedContexts, updateNote) => ({
  id: workflowId,
  expectedVersion,
  boundedContexts,
  updateNote
})

export const buildCreateBoundedContextMutation = ({ workflow, workflowId, name }) => {
  if (!name || name.trim() === '') {
    return { error: 'name is required' }
  }

  const boundedContexts = workflow.boundedContexts || []

  if (boundedContexts.find((bc) => bc.name === name)) {
    return { error: `Bounded context "${name}" already exists` }
  }

  const updatedBoundedContexts = [...boundedContexts, { id: generateId(), name }]

  return {
    mutationInput: buildBoundedContextsMutationInput(workflowId, workflow.version, updatedBoundedContexts, {
      action: 'added bounded context',
      target: name
    }),
    name
  }
}

export const buildUpdateBoundedContextMutation = ({ workflow, workflowId, boundedContext, name }) => {
  if (!name || name.trim() === '') {
    return { error: 'name is required' }
  }

  const boundedContexts = workflow.boundedContexts || []
  const boundedContextIndex = boundedContexts.findIndex((bc) => bc.name === boundedContext)

  if (boundedContextIndex === -1) {
    return { error: `Bounded context "${boundedContext}" not found` }
  }

  const matchedBoundedContext = boundedContexts[boundedContextIndex]

  if (boundedContexts.find((bc) => bc.name === name && bc.id !== matchedBoundedContext.id)) {
    return { error: `Bounded context "${name}" already exists` }
  }

  const updatedBoundedContexts = [...boundedContexts]
  updatedBoundedContexts[boundedContextIndex] = { ...matchedBoundedContext, name }

  return {
    mutationInput: buildBoundedContextsMutationInput(workflowId, workflow.version, updatedBoundedContexts, {
      action: 'renamed bounded context',
      target: `${boundedContext} → ${name}`
    }),
    boundedContext,
    name
  }
}

export const buildDeleteBoundedContextMutation = ({ workflow, workflowId, boundedContext }) => {
  if (!boundedContext) {
    return { error: 'boundedContext is required' }
  }

  const boundedContexts = workflow.boundedContexts || []
  const matchedBoundedContext = boundedContexts.find((bc) => bc.name === boundedContext)

  if (!matchedBoundedContext) {
    return { error: `Bounded context "${boundedContext}" not found` }
  }

  const assignedEntities = (workflow.schemas || []).filter(
    (schema) => schema.type === SchemaType.ENTITY && schema.boundedContext === matchedBoundedContext.id
  )
  if (assignedEntities.length > 0) {
    const entityNames = assignedEntities.map((entity) => entity.name).join(', ')

    return {
      error: `Cannot delete bounded context "${boundedContext}": it has ${assignedEntities.length} assigned entity(ies): ${entityNames}. Detach all entities first.`
    }
  }

  const updatedBoundedContexts = boundedContexts.filter((bc) => bc.id !== matchedBoundedContext.id)

  return {
    mutationInput: buildBoundedContextsMutationInput(workflowId, workflow.version, updatedBoundedContexts, {
      action: 'deleted bounded context',
      target: boundedContext
    }),
    // The caller must run the standalone deleteBoundedContext mutation with this id BEFORE the workflow update.
    boundedContextId: matchedBoundedContext.id,
    boundedContext
  }
}
